import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n';
import api from '../../api';
import './GuardianWhisperBubble.css';

function getRouteCTA(pathname, targetAction, navigate, t) {
  if (pathname.startsWith('/profile') || targetAction === 'PROFILE_REGENERATE') {
    return {
      label: t('guardian.cta.refineProfile'),
      action: () => {
        if (!pathname.startsWith('/profile')) {
          navigate('/profile');
        } else {
          document.querySelector('.profile-feedback-frame')?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };
  } else if (pathname.startsWith('/hiperstision')) {
    return {
      label: t('guardian.cta.tellQoph'),
      action: () => navigate('/qoph')
    };
  } else if (pathname.startsWith('/tareas') || pathname.startsWith('/lugares') || targetAction === 'TASK_GENERATE') {
    return {
      label: t('guardian.cta.newTask'),
      action: () => {
        if (!pathname.startsWith('/tareas') && !pathname.startsWith('/lugares')) {
          navigate('/tareas');
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    };
  } else if (pathname.startsWith('/conciencia')) {
    return {
      label: t('guardian.cta.delveDeeper'),
      action: () => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
    };
  } else if (pathname.startsWith('/qoph') || targetAction === 'QOPH_MESSAGE') {
    return {
      label: t('guardian.cta.newThought'),
      action: () => {
        if (!pathname.startsWith('/qoph')) {
          navigate('/qoph');
        } else {
          const inputContainer = document.querySelector('.cyber-input-container');
          if (inputContainer) {
            inputContainer.scrollIntoView({ behavior: 'smooth' });
            const inputField = inputContainer.querySelector('textarea, input');
            if (inputField) inputField.focus();
          }
        }
      }
    };
  } else if (pathname.startsWith('/subconciencia') || targetAction === 'SUBCONCIENCIA_EXPLORE') {
    return {
      label: t('guardian.cta.enterDream'),
      action: () => {
        if (!pathname.startsWith('/subconciencia')) {
          navigate('/subconciencia');
        } else {
          document.querySelector('.subconciencia-stage')?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };
  } else if (pathname.startsWith('/inquisition') || targetAction === 'INQUISITION_AUDIT') {
    return {
      label: t('guardian.cta.viewAnomalies'),
      action: () => {
        if (!pathname.startsWith('/inquisition')) {
          navigate('/inquisition');
        } else {
          document.querySelector('.anomaly-list')?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };
  } else if (pathname === '/' || targetAction === 'MUTANT_BOX_GENERATE') {
    return {
      label: t('guardian.cta.exploreMutantBox'),
      action: () => {
        if (pathname !== '/') {
          navigate('/', { state: { scrollToMutantBox: true } });
        } else {
          const mutantBox = document.querySelector('#mutant-box-container') ||
                            document.querySelector('#mutant-box-main') ||
                            document.querySelector('.mutantbox-root');
          if (mutantBox) {
            mutantBox.scrollIntoView({ behavior: 'smooth' });
          } else {
            navigate('/qoph');
          }
        }
      }
    };
  }
  return null;
}

export default function GuardianWhisperBubble({ replayTrigger }) {
  const { language, t } = useI18n();
  const [whisper, setWhisper] = useState(null);
  const [targetAction, setTargetAction] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [displayedText, setDisplayedText] = useState("");
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch Whisper
  useEffect(() => {
    let isMounted = true;
    const fetchWhisper = async () => {
      try {
        const response = await api.get(`/agent/whispers?route=${encodeURIComponent(location.pathname)}&lang=${language}`);
        if (isMounted && response.data && response.data.message) {
          setWhisper(response.data.message);
          setTargetAction(response.data.target_action || null);
        }
      } catch (error) {
        console.error('Failed to fetch guardian whisper:', error);
      }
    };

    const fetchTimer = setTimeout(fetchWhisper, 1000);
    return () => {
      isMounted = false;
      clearTimeout(fetchTimer);
    };
  }, [location.pathname, language]);

  // Typewriter Effect
  useEffect(() => {
    let typingInterval = null;
    let hideTimer = null;
    if (whisper) {
      setIsVisible(true);
      setDisplayedText("");
      setIsTypingComplete(false);
      let i = 1;
      typingInterval = setInterval(() => {
        setDisplayedText(whisper.slice(0, i));
        if (i >= whisper.length) {
          clearInterval(typingInterval);
          setIsTypingComplete(true);
          hideTimer = setTimeout(() => {
            setIsVisible(false);
          }, 12000);
        }
        i++;
      }, 30);
    }
    return () => {
      if (typingInterval) clearInterval(typingInterval);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [whisper, replayTrigger]);

  if (!whisper || !isVisible) return null;

  const cta = getRouteCTA(location.pathname, targetAction, navigate, t);

  return (
    <div
      key={replayTrigger}
      className="guardian-whisper-bubble glass-panel"
      onClick={(e) => {
        e.stopPropagation();
        setIsVisible(false);
      }}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsVisible(false);
        }}
        style={{
          position: 'absolute',
          top: '4px',
          right: '8px',
          background: 'transparent',
          border: 'none',
          color: 'inherit',
          opacity: 0.6,
          cursor: 'pointer',
          fontSize: '14px',
          padding: '4px'
        }}
        aria-label="Close"
      >
        &times;
      </button>
      <p className="typewriter-text">{displayedText}</p>
      {isTypingComplete && cta && (
        <button
          className="whisper-cta-pill"
          onClick={(e) => {
            e.stopPropagation();
            if (cta.action) cta.action();
            setIsVisible(false);
          }}
        >
          {cta.label}
        </button>
      )}
    </div>
  );
}
